/**
 * Building Data
 * 
 * Level-based building properties from buildings.md
 * All values are per hour unless otherwise specified
 */

import {
    WALL, CUTTER, QUARRY, MINE, FARM, CASA, CELLARIUM, CACHE,
    MANOR, BAZAAR, SAWMILL, STONEMASON, MILL, FOUNDRY,
    BARRACKS, CITY_GUARD_HOUSE, TRAINING_GROUND, STABLES, MOONGLOW,
    TEMPLE, CASTLE, WORKSHOP, WHARF, SHIPYARD
} from './Constants.js';

// Lumber Mill, Quarry, Iron Mine production (same values for all)
export const GATHERER_PRODUCTION = {
    1: 20, 2: 40, 3: 60, 4: 85, 5: 110,
    6: 140, 7: 175, 8: 210, 9: 250, 10: 300
};

// Manor production
export const FARM_PRODUCTION = {
    1: 5, 2: 8, 3: 15, 4: 20, 5: 30,
    6: 45, 7: 75, 8: 120, 9: 200, 10: 300
};

// Estate (Townhouse) gold production
export const VILLA_PRODUCTION = {
    1: 25, 2: 50, 3: 75, 4: 100, 5: 130,
    6: 170, 7: 210, 8: 260, 9: 320, 10: 400
};

// Casa production bonus (percentage)
export const CASA_PRODUCTION_BONUS = {
    1: 0.03, 2: 0.06, 3: 0.09, 4: 0.12, 5: 0.15,
    6: 0.18, 7: 0.21, 8: 0.24, 9: 0.27, 10: 0.30
};

// Refinery production bonus (percentage) - Sawmill, Mason's Workshop, Forge, Mill
export const REFINERY_PRODUCTION_BONUS = {
    1: 0.30, 2: 0.35, 3: 0.40, 4: 0.45, 5: 0.50,
    6: 0.55, 7: 0.60, 8: 0.65, 9: 0.70, 10: 0.75
};

// Wharf tax bonus (percentage)
export const WHARF_TAX_BONUS = {
    1: 0.05, 2: 0.10, 3: 0.15, 4: 0.20, 5: 0.25,
    6: 0.30, 7: 0.35, 8: 0.40, 9: 0.45, 10: 0.50
};

// Bazaar tax bonus (percentage)
export const BAZAAR_TAX_BONUS = {
    1: 0.02, 2: 0.04, 3: 0.06, 4: 0.08, 5: 0.10,
    6: 0.12, 7: 0.14, 8: 0.16, 9: 0.18, 10: 0.20
};

// Cellarium storage capacity
export const CELLARIUM_STORAGE = {
    1: 2500, 2: 5000, 3: 9000, 4: 16000, 5: 26000,
    6: 42000, 7: 65000, 8: 100000, 9: 145000, 10: 200000
};

// Cache hidden storage capacity
export const CACHE_STORAGE = {
    1: 500, 2: 1000, 3: 2000, 4: 3000, 5: 4500,
    6: 6000, 7: 8000, 8: 10000, 9: 12000, 10: 15000
};

// Keep storage capacity
export const KEEP_STORAGE = {
    1: 5000, 2: 7000, 3: 10000, 4: 15000, 5: 24000,
    6: 35000, 7: 50000, 8: 80000, 9: 125000, 10: 175000
};

// Keep wood production (constant 300 per hour at all levels)
export const BASILICA_WOOD_PRODUCTION = 300;

// Keep building slots (total slots available)
export const BASILICA_BUILDING_SLOTS = {
    1: 10, 2: 20, 3: 30, 4: 40, 5: 50,
    6: 60, 7: 70, 8: 80, 9: 90, 10: 100
};

// Building to Keep level requirements (from buildings.md)
// Maps building constant values to the minimum Keep level required to build them
export const BUILDING_KEEP_REQUIREMENTS = {
    // Level 1 buildings
    [CUTTER]: 1,          // Lumber Mill
    [CASA]: 1,            // Casa
    [CELLARIUM]: 1,       // Cellarium
    
    // Level 2 buildings
    [WALL]: 2,            // Fortress Wall
    [QUARRY]: 2,          // Quarry
    [CACHE]: 2,           // Cache
    
    // Level 3 buildings
    [FARM]: 3,            // Manor
    [CITY_GUARD_HOUSE]: 3, // Guard Tower
    
    // Level 4 buildings
    [MINE]: 4,            // Iron Mine
    [BARRACKS]: 4,        // Barracks
    [TRAINING_GROUND]: 4, // Training Grounds
    
    // Level 5 buildings
    [MANOR]: 5,           // Manor
    [BAZAAR]: 5,          // Bazaar
    
    // Level 6 buildings
    [SAWMILL]: 6,         // Sawmill
    [STABLES]: 6,         // Stables
    
    // Level 7 buildings
    [STONEMASON]: 7,      // Mason's Workshop
    [MOONGLOW]: 7,        // Mage Tower
    
    // Level 8 buildings
    [MILL]: 8,            // Mill
    [TEMPLE]: 8,          // War College
    [CASTLE]: 8,          // Fortress
    
    // Level 9 buildings
    [FOUNDRY]: 9,         // Forge
    [WORKSHOP]: 9,        // Siege Workshop
    
    // Level 10 buildings
    [WHARF]: 10,          // Wharf
    [SHIPYARD]: 10        // Shipyard
};

// Helper function to parse build time strings to seconds
// Examples: "15 sec" -> 15, "1 hr 41 min 15 sec" -> 6075, "22 hr 42 min" -> 81720
/**
 * Parse time string with days support
 */
function parseBuildTimeWithDays(timeStr) {
    if (!timeStr || timeStr === '-') return 0;
    
    let totalSeconds = 0;
    
    // Handle days
    const dayMatch = timeStr.match(/(\d+)\s*day/i);
    if (dayMatch) {
        totalSeconds += parseInt(dayMatch[1]) * 24 * 60 * 60;
    }
    
    // Handle hours
    const hrMatch = timeStr.match(/(\d+)\s*hr/i);
    if (hrMatch) {
        totalSeconds += parseInt(hrMatch[1]) * 60 * 60;
    }
    
    // Handle minutes
    const minMatch = timeStr.match(/(\d+)\s*min/i);
    if (minMatch) {
        totalSeconds += parseInt(minMatch[1]) * 60;
    }
    
    // Handle seconds
    const secMatch = timeStr.match(/(\d+)\s*sec/i);
    if (secMatch) {
        totalSeconds += parseInt(secMatch[1]);
    }
    
    return totalSeconds;
}

function parseBuildTime(timeStr) {
    if (!timeStr || timeStr === '-') return 0;
    
    let totalSeconds = 0;
    const parts = timeStr.toLowerCase().split(/\s+/);
    
    for (let i = 0; i < parts.length; i += 2) {
        const value = parseInt(parts[i]);
        const unit = parts[i + 1] || '';
        
        if (unit.includes('hr') || unit.includes('hour')) {
            totalSeconds += value * 3600;
        } else if (unit.includes('min')) {
            totalSeconds += value * 60;
        } else if (unit.includes('sec')) {
            totalSeconds += value;
        }
    }
    
    return totalSeconds;
}

// Building costs (Wood, Stone) and build times - from buildings.md
// Format: { level: { wood: cost, stone: cost, buildTime: seconds } }
export const BUILDING_COSTS = {
    // Lumber Mill, Quarry, Iron Mine (same costs)
    CUTTER: {
        1: { wood: 50, stone: 0, buildTime: parseBuildTime('15 sec') },
        2: { wood: 200, stone: 0, buildTime: parseBuildTime('54 sec') },
        3: { wood: 400, stone: 200, buildTime: parseBuildTime('6 min') },
        4: { wood: 1400, stone: 600, buildTime: parseBuildTime('45 min') },
        5: { wood: 3500, stone: 1500, buildTime: parseBuildTime('1 hr 41 min 15 sec') },
        6: { wood: 6000, stone: 3000, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        7: { wood: 10000, stone: 5000, buildTime: parseBuildTime('6 hr 15 min') },
        8: { wood: 16000, stone: 8000, buildTime: parseBuildTime('9 hr 57 min') },
        9: { wood: 25000, stone: 13000, buildTime: parseBuildTime('14 hr 58 min') },
        10: { wood: 38000, stone: 20000, buildTime: parseBuildTime('22 hr 42 min') }
    },
    QUARRY: {
        1: { wood: 50, stone: 0, buildTime: parseBuildTime('15 sec') },
        2: { wood: 200, stone: 0, buildTime: parseBuildTime('54 sec') },
        3: { wood: 400, stone: 200, buildTime: parseBuildTime('6 min') },
        4: { wood: 1400, stone: 600, buildTime: parseBuildTime('45 min') },
        5: { wood: 3500, stone: 1500, buildTime: parseBuildTime('1 hr 41 min 15 sec') },
        6: { wood: 6000, stone: 3000, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        7: { wood: 10000, stone: 5000, buildTime: parseBuildTime('6 hr 15 min') },
        8: { wood: 16000, stone: 8000, buildTime: parseBuildTime('9 hr 57 min') },
        9: { wood: 25000, stone: 13000, buildTime: parseBuildTime('14 hr 58 min') },
        10: { wood: 38000, stone: 20000, buildTime: parseBuildTime('22 hr 42 min') }
    },
    MINE: {
        1: { wood: 50, stone: 0, buildTime: parseBuildTime('15 sec') },
        2: { wood: 200, stone: 0, buildTime: parseBuildTime('54 sec') },
        3: { wood: 400, stone: 200, buildTime: parseBuildTime('6 min') },
        4: { wood: 1400, stone: 600, buildTime: parseBuildTime('45 min') },
        5: { wood: 3500, stone: 1500, buildTime: parseBuildTime('1 hr 41 min 15 sec') },
        6: { wood: 6000, stone: 3000, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        7: { wood: 10000, stone: 5000, buildTime: parseBuildTime('6 hr 15 min') },
        8: { wood: 16000, stone: 8000, buildTime: parseBuildTime('9 hr 57 min') },
        9: { wood: 25000, stone: 13000, buildTime: parseBuildTime('14 hr 58 min') },
        10: { wood: 38000, stone: 20000, buildTime: parseBuildTime('22 hr 42 min') }
    },
    // Manor
    FARM: {
        1: { wood: 50, stone: 0, buildTime: parseBuildTime('15 sec') },
        2: { wood: 200, stone: 0, buildTime: parseBuildTime('54 sec') },
        3: { wood: 400, stone: 200, buildTime: parseBuildTime('6 min') },
        4: { wood: 1400, stone: 600, buildTime: parseBuildTime('45 min') },
        5: { wood: 3500, stone: 1500, buildTime: parseBuildTime('1 hr 41 min 15 sec') },
        6: { wood: 6000, stone: 3000, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        7: { wood: 10000, stone: 5000, buildTime: parseBuildTime('6 hr 15 min') },
        8: { wood: 16000, stone: 8000, buildTime: parseBuildTime('9 hr 57 min') },
        9: { wood: 25000, stone: 13000, buildTime: parseBuildTime('14 hr 58 min') },
        10: { wood: 38000, stone: 20000, buildTime: parseBuildTime('22 hr 42 min') }
    },
    // Manor - from buildings.md line 280-290
    MANOR: {
        1: { wood: 0, stone: 100, buildTime: parseBuildTime('17 sec') },
        2: { wood: 0, stone: 300, buildTime: parseBuildTime('1 min 12 sec') },
        3: { wood: 0, stone: 600, buildTime: parseBuildTime('8 min') },
        4: { wood: 0, stone: 2000, buildTime: parseBuildTime('1 hr') },
        5: { wood: 1000, stone: 4000, buildTime: parseBuildTime('2 hr 15 min') },
        6: { wood: 2000, stone: 7000, buildTime: parseBuildTime('4 hr 30 min') },
        7: { wood: 3500, stone: 11500, buildTime: parseBuildTime('8 hr 12 min') },
        8: { wood: 7000, stone: 17000, buildTime: parseBuildTime('13 hr 15 min') },
        9: { wood: 14000, stone: 20000, buildTime: parseBuildTime('19 hr 57 min') },
        10: { wood: 29000, stone: 29000, buildTime: parseBuildTime('30 hr 15 min') }
    },
    // Casa
    CASA: {
        1: { wood: 0, stone: 50, buildTime: parseBuildTime('15 sec') },
        2: { wood: 0, stone: 150, buildTime: parseBuildTime('54 sec') },
        3: { wood: 0, stone: 300, buildTime: parseBuildTime('6 min') },
        4: { wood: 0, stone: 600, buildTime: parseBuildTime('45 min') },
        5: { wood: 200, stone: 1000, buildTime: parseBuildTime('1 hr 41 min 15 sec') },
        6: { wood: 500, stone: 2000, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        7: { wood: 1000, stone: 4000, buildTime: parseBuildTime('6 hr 15 min') },
        8: { wood: 2000, stone: 7500, buildTime: parseBuildTime('9 hr 57 min') },
        9: { wood: 5000, stone: 14000, buildTime: parseBuildTime('14 hr 58 min') },
        10: { wood: 12000, stone: 17000, buildTime: parseBuildTime('22 hr 42 min') }
    },
    // Cellarium
    CELLARIUM: {
        1: { wood: 60, stone: 0, buildTime: parseBuildTime('15 sec') },
        2: { wood: 150, stone: 0, buildTime: parseBuildTime('36 sec') },
        3: { wood: 250, stone: 50, buildTime: parseBuildTime('4 min') },
        4: { wood: 500, stone: 150, buildTime: parseBuildTime('30 min') },
        5: { wood: 1600, stone: 400, buildTime: parseBuildTime('1 hr 7 min 30 sec') },
        6: { wood: 3000, stone: 1000, buildTime: parseBuildTime('2 hr 15 min') },
        7: { wood: 6000, stone: 2000, buildTime: parseBuildTime('4 hr 6 min') },
        8: { wood: 9600, stone: 4800, buildTime: parseBuildTime('6 hr 37 min 30 sec') },
        9: { wood: 15000, stone: 9000, buildTime: parseBuildTime('9 hr 58 min 30 sec') },
        10: { wood: 20000, stone: 13000, buildTime: parseBuildTime('15 hr 7 min 30 sec') }
    },
    // Cache
    CACHE: {
        1: { wood: 0, stone: 50, buildTime: parseBuildTime('15 sec') },
        2: { wood: 0, stone: 200, buildTime: parseBuildTime('36 sec') },
        3: { wood: 0, stone: 600, buildTime: parseBuildTime('4 min') },
        4: { wood: 0, stone: 1000, buildTime: parseBuildTime('30 min') },
        5: { wood: 0, stone: 1500, buildTime: parseBuildTime('1 hr 7 min 30 sec') },
        6: { wood: 0, stone: 2200, buildTime: parseBuildTime('2 hr 15 min') },
        7: { wood: 0, stone: 3500, buildTime: parseBuildTime('4 hr 6 min') },
        8: { wood: 0, stone: 4500, buildTime: parseBuildTime('6 hr 37 min 30 sec') },
        9: { wood: 0, stone: 6000, buildTime: parseBuildTime('9 hr 58 min 30 sec') },
        10: { wood: 0, stone: 8000, buildTime: parseBuildTime('15 hr 7 min 30 sec') }
    },
    // Wharf - from buildings.md line 552-562
    WHARF: {
        1: { wood: 80, stone: 40, buildTime: parseBuildTime('22 sec') },
        2: { wood: 160, stone: 80, buildTime: parseBuildTime('2 min 24 sec') },
        3: { wood: 320, stone: 160, buildTime: parseBuildTime('16 min') },
        4: { wood: 800, stone: 400, buildTime: parseBuildTime('2 hr') },
        5: { wood: 2400, stone: 1200, buildTime: parseBuildTime('4 hr 30 min') },
        6: { wood: 5600, stone: 2800, buildTime: parseBuildTime('9 hr') },
        7: { wood: 11200, stone: 5600, buildTime: parseBuildTime('16 hr 24 min') },
        8: { wood: 19200, stone: 9600, buildTime: parseBuildTime('26 hr 30 min') },
        9: { wood: 30400, stone: 15200, buildTime: parseBuildTime('39 hr 54 min') },
        10: { wood: 46400, stone: 23200, buildTime: parseBuildTime('60 hr 30 min') }
    },
    // Bazaar - from buildings.md line 296-306
    BAZAAR: {
        1: { wood: 40, stone: 20, buildTime: parseBuildTime('20 sec') },
        2: { wood: 80, stone: 40, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 160, stone: 80, buildTime: parseBuildTime('12 min') },
        4: { wood: 400, stone: 200, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 1200, stone: 600, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 2800, stone: 1400, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 5600, stone: 2800, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 9600, stone: 4800, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 15200, stone: 7600, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 23200, stone: 11600, buildTime: parseBuildTime('45 hr 22 min 30 sec') }
    },
    // Keep (Town Hall) - from buildings.md line 56-66
    TOWN_HALL: {
        1: { wood: 0, stone: 0, buildTime: 0 }, // Cannot be built, starts at level 1
        2: { wood: 200, stone: 0, buildTime: parseBuildTime('25 sec') },
        3: { wood: 500, stone: 100, buildTime: parseBuildTime('40 sec') },
        4: { wood: 1000, stone: 300, buildTime: parseBuildTime('1 min 40 sec') },
        5: { wood: 3000, stone: 1500, buildTime: parseBuildTime('30 min') },
        6: { wood: 8000, stone: 4000, buildTime: parseBuildTime('2 hr 30 min') },
        7: { wood: 15000, stone: 10000, buildTime: parseBuildTime('8 hr 20 min') },
        8: { wood: 30000, stone: 25000, buildTime: parseBuildTime('16 hr 40 min') },
        9: { wood: 60000, stone: 60000, buildTime: parseBuildTime('26 hr') },
        10: { wood: 120000, stone: 120000, buildTime: parseBuildTime('37 hr 20 min') }
    },
    // Fortress Wall - from buildings.md line 120-130
    WALL: {
        1: { wood: 0, stone: 200, buildTime: parseBuildTime('30 sec') },
        2: { wood: 0, stone: 2000, buildTime: parseBuildTime('20 min') },
        3: { wood: 0, stone: 8000, buildTime: parseBuildTime('1 hr') },
        4: { wood: 0, stone: 20000, buildTime: parseBuildTime('3 hr') },
        5: { wood: 0, stone: 30000, buildTime: parseBuildTime('5 hr') },
        6: { wood: 0, stone: 45000, buildTime: parseBuildTime('8 hr') },
        7: { wood: 0, stone: 70000, buildTime: parseBuildTime('12 hr') },
        8: { wood: 0, stone: 100000, buildTime: parseBuildTime('18 hr') },
        9: { wood: 0, stone: 140000, buildTime: parseBuildTime('25 hr') },
        10: { wood: 0, stone: 200000, buildTime: parseBuildTime('35 hr') }
    },
    // Sawmill - from buildings.md line 328-338
    SAWMILL: {
        1: { wood: 60, stone: 60, buildTime: parseBuildTime('20 sec') },
        2: { wood: 150, stone: 150, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 350, stone: 350, buildTime: parseBuildTime('12 min') },
        4: { wood: 1100, stone: 1100, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 2700, stone: 2700, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 5000, stone: 5000, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 8500, stone: 8500, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 13500, stone: 13500, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 21500, stone: 21500, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 33000, stone: 33000, buildTime: parseBuildTime('45 hr 22 min 30 sec') }
    },
    // Mason's Workshop (Stonemason) - from buildings.md line 376-386
    STONEMASON: {
        1: { wood: 60, stone: 60, buildTime: parseBuildTime('20 sec') },
        2: { wood: 150, stone: 150, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 350, stone: 350, buildTime: parseBuildTime('12 min') },
        4: { wood: 1100, stone: 1100, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 2700, stone: 2700, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 5000, stone: 5000, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 8500, stone: 7800, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 13500, stone: 13500, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 21500, stone: 21500, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 33000, stone: 33000, buildTime: parseBuildTime('45 hr 22 min 30 sec') }
    },
    // Forge (Foundry) - from buildings.md line 504-514
    FOUNDRY: {
        1: { wood: 60, stone: 60, buildTime: parseBuildTime('20 sec') },
        2: { wood: 150, stone: 150, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 350, stone: 350, buildTime: parseBuildTime('12 min') },
        4: { wood: 1100, stone: 1100, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 2700, stone: 2700, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 5000, stone: 5000, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 8500, stone: 8500, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 13500, stone: 13500, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 21500, stone: 21500, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 33000, stone: 33000, buildTime: parseBuildTime('45 hr 22 min 30 sec') }
    },
    // Mill - from buildings.md line 424-434
    MILL: {
        1: { wood: 60, stone: 60, buildTime: parseBuildTime('20 sec') },
        2: { wood: 150, stone: 150, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 350, stone: 350, buildTime: parseBuildTime('12 min') },
        4: { wood: 1100, stone: 1100, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 2700, stone: 2700, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 5000, stone: 5000, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 8500, stone: 8500, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 13500, stone: 13500, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 21500, stone: 21500, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 33000, stone: 33000, buildTime: parseBuildTime('49 hr 22 min 30 sec') }
    },
    // Barracks - from buildings.md line 248-258
    BARRACKS: {
        1: { wood: 0, stone: 50, buildTime: parseBuildTime('15 sec') },
        2: { wood: 0, stone: 150, buildTime: parseBuildTime('36 sec') },
        3: { wood: 0, stone: 300, buildTime: parseBuildTime('4 min') },
        4: { wood: 0, stone: 600, buildTime: parseBuildTime('30 min') },
        5: { wood: 0, stone: 1200, buildTime: parseBuildTime('1 hr 7 min 30 sec') },
        6: { wood: 0, stone: 2500, buildTime: parseBuildTime('2 hr 15 min') },
        7: { wood: 0, stone: 4000, buildTime: parseBuildTime('4 hr 6 min') },
        8: { wood: 0, stone: 7000, buildTime: parseBuildTime('6 hr 37 min 30 sec') },
        9: { wood: 0, stone: 11500, buildTime: parseBuildTime('9 hr 58 min 30 sec') },
        10: { wood: 0, stone: 17500, buildTime: parseBuildTime('15 hr 7 min 30 sec') }
    },
    // Guard Tower (City Guard House) - from buildings.md line 200-210
    CITY_GUARD_HOUSE: {
        1: { wood: 15, stone: 30, buildTime: parseBuildTime('17 sec') },
        2: { wood: 30, stone: 60, buildTime: parseBuildTime('1 min 12 sec') },
        3: { wood: 55, stone: 110, buildTime: parseBuildTime('8 min') },
        4: { wood: 140, stone: 280, buildTime: parseBuildTime('1 hr') },
        5: { wood: 400, stone: 800, buildTime: parseBuildTime('2 hr 15 min') },
        6: { wood: 1000, stone: 2000, buildTime: parseBuildTime('4 hr 30 min') },
        7: { wood: 1900, stone: 3800, buildTime: parseBuildTime('8 hr 12 min') },
        8: { wood: 3200, stone: 6400, buildTime: parseBuildTime('13 hr 15 min') },
        9: { wood: 5100, stone: 10200, buildTime: parseBuildTime('19 hr 57 min') },
        10: { wood: 8000, stone: 16000, buildTime: parseBuildTime('30 hr 15 min') }
    },
    // Training Grounds (Training Ground) - from buildings.md line 264-274
    TRAINING_GROUND: {
        1: { wood: 20, stone: 40, buildTime: parseBuildTime('20 sec') },
        2: { wood: 40, stone: 80, buildTime: parseBuildTime('1 min 48 sec') },
        3: { wood: 80, stone: 160, buildTime: parseBuildTime('12 min') },
        4: { wood: 200, stone: 400, buildTime: parseBuildTime('1 hr 30 min') },
        5: { wood: 600, stone: 1200, buildTime: parseBuildTime('3 hr 22 min 30 sec') },
        6: { wood: 1400, stone: 2800, buildTime: parseBuildTime('6 hr 45 min') },
        7: { wood: 2800, stone: 5600, buildTime: parseBuildTime('12 hr 18 min') },
        8: { wood: 4800, stone: 9600, buildTime: parseBuildTime('19 hr 52 min 30 sec') },
        9: { wood: 7500, stone: 15000, buildTime: parseBuildTime('29 hr 55 min 30 sec') },
        10: { wood: 11500, stone: 23000, buildTime: parseBuildTime('45 hr 22 min 30 sec') }
    },
    // Stables - from buildings.md line 344-354
    STABLES: {
        1: { wood: 25, stone: 50, buildTime: parseBuildTime('22 sec') },
        2: { wood: 55, stone: 110, buildTime: parseBuildTime('2 min 24 sec') },
        3: { wood: 110, stone: 220, buildTime: parseBuildTime('16 min') },
        4: { wood: 275, stone: 550, buildTime: parseBuildTime('2 hr') },
        5: { wood: 800, stone: 1600, buildTime: parseBuildTime('4 hr 30 min') },
        6: { wood: 1900, stone: 3800, buildTime: parseBuildTime('9 hr') },
        7: { wood: 3750, stone: 7500, buildTime: parseBuildTime('16 hr 24 min') },
        8: { wood: 6500, stone: 13000, buildTime: parseBuildTime('26 hr 30 min') },
        9: { wood: 10200, stone: 20400, buildTime: parseBuildTime('39 hr 54 min') },
        10: { wood: 15500, stone: 31000, buildTime: parseBuildTime('60 hr 30 min') }
    },
    // Mage Tower (Moonglow) - from buildings.md line 392-402
    MOONGLOW: {
        1: { wood: 30, stone: 60, buildTime: parseBuildTime('22 sec') },
        2: { wood: 60, stone: 120, buildTime: parseBuildTime('2 min 24 sec') },
        3: { wood: 120, stone: 240, buildTime: parseBuildTime('16 min') },
        4: { wood: 300, stone: 600, buildTime: parseBuildTime('2 hr') },
        5: { wood: 900, stone: 1800, buildTime: parseBuildTime('4 hr 30 min') },
        6: { wood: 2100, stone: 4200, buildTime: parseBuildTime('9 hr') },
        7: { wood: 4200, stone: 8400, buildTime: parseBuildTime('16 hr 24 min') },
        8: { wood: 7200, stone: 14400, buildTime: parseBuildTime('26 hr 30 min') },
        9: { wood: 11400, stone: 22800, buildTime: parseBuildTime('39 hr 54 min') },
        10: { wood: 17400, stone: 34800, buildTime: parseBuildTime('60 hr 30 min') }
    },
    // Shipyard - from buildings.md line 568-578
    SHIPYARD: {
        1: { wood: 50, stone: 100, buildTime: parseBuildTime('27 sec') },
        2: { wood: 100, stone: 200, buildTime: parseBuildTime('3 min 36 sec') },
        3: { wood: 200, stone: 400, buildTime: parseBuildTime('24 min') },
        4: { wood: 500, stone: 1000, buildTime: parseBuildTime('3 hr') },
        5: { wood: 1500, stone: 3000, buildTime: parseBuildTime('6 hr 45 min') },
        6: { wood: 3500, stone: 7000, buildTime: parseBuildTime('13 hr 30 min') },
        7: { wood: 7000, stone: 14000, buildTime: parseBuildTime('24 hr 36 min') },
        8: { wood: 12000, stone: 24000, buildTime: parseBuildTime('29 hr 45 min') },
        9: { wood: 19000, stone: 38000, buildTime: parseBuildTime('59 hr 51 min') },
        10: { wood: 29000, stone: 58000, buildTime: parseBuildTime('90 hr 45 min') }
    },
    // Siege Workshop (Workshop) - from buildings.md line 520-530
    WORKSHOP: {
        1: { wood: 40, stone: 80, buildTime: parseBuildTime('25 sec') },
        2: { wood: 75, stone: 150, buildTime: parseBuildTime('3 min') },
        3: { wood: 150, stone: 300, buildTime: parseBuildTime('20 min') },
        4: { wood: 370, stone: 740, buildTime: parseBuildTime('2 hr 30 min') },
        5: { wood: 1100, stone: 2200, buildTime: parseBuildTime('5 hr 37 min 30 sec') },
        6: { wood: 2600, stone: 5200, buildTime: parseBuildTime('11 hr 15 min') },
        7: { wood: 5200, stone: 10400, buildTime: parseBuildTime('20 hr 30 min') },
        8: { wood: 8900, stone: 17800, buildTime: parseBuildTime('33 hr 7 min 30 sec') },
        9: { wood: 14000, stone: 28000, buildTime: parseBuildTime('49 hr 52 min 30 sec') },
        10: { wood: 21500, stone: 43000, buildTime: parseBuildTime('75 hr 37 min 30 sec') }
    },
    // Fortress - from buildings.md line 456-466
    CASTLE: {
        1: { wood: 0, stone: 20000, buildTime: parseBuildTime('20 hr') },
        2: { wood: 0, stone: 25000, buildTime: parseBuildTime('23 hr 20 min') },
        3: { wood: 0, stone: 30000, buildTime: parseBuildTimeWithDays('1 day 2 hr 40 min') },
        4: { wood: 0, stone: 40000, buildTime: parseBuildTimeWithDays('1 day 6 hr') },
        5: { wood: 0, stone: 55000, buildTime: parseBuildTimeWithDays('1 day 9 hr 20 min') },
        6: { wood: 0, stone: 75000, buildTime: parseBuildTimeWithDays('1 day 12 hr 40 min') },
        7: { wood: 0, stone: 100000, buildTime: parseBuildTimeWithDays('1 day 16 hr') },
        8: { wood: 0, stone: 130000, buildTime: parseBuildTimeWithDays('1 day 19 hr 20 min') },
        9: { wood: 0, stone: 160000, buildTime: parseBuildTimeWithDays('1 day 22 hr 40 min') },
        10: { wood: 0, stone: 200000, buildTime: parseBuildTimeWithDays('2 day 2 hr') }
    }
};

/**
 * Get production value for a gatherer building (Lumber Mill, Quarry, Iron Mine)
 */
export function getGathererProduction(level) {
    return GATHERER_PRODUCTION[level] || GATHERER_PRODUCTION[10];
}

/**
 * Get production value for a farm
 */
export function getFarmProduction(level) {
    return FARM_PRODUCTION[level] || FARM_PRODUCTION[10];
}

/**
 * Get gold production for a villa
 */
export function getEstateProduction(level) {
    return VILLA_PRODUCTION[level] || VILLA_PRODUCTION[10];
}

/**
 * Get casa production bonus multiplier
 */
export function getCottageBonus(level) {
    return CASA_PRODUCTION_BONUS[level] || CASA_PRODUCTION_BONUS[10];
}

/**
 * Get refinery production bonus multiplier
 */
export function getRefineryBonus(level) {
    return REFINERY_PRODUCTION_BONUS[level] || REFINERY_PRODUCTION_BONUS[10];
}

/**
 * Get wharf tax bonus multiplier
 */
export function getHarborBonus(level) {
    return WHARF_TAX_BONUS[level] || WHARF_TAX_BONUS[10];
}

/**
 * Get bazaar tax bonus multiplier
 */
export function getMarketBonus(level) {
    return BAZAAR_TAX_BONUS[level] || BAZAAR_TAX_BONUS[10];
}

/**
 * Get cellarium storage capacity
 */
export function getGranaryStorage(level) {
    return CELLARIUM_STORAGE[level] || CELLARIUM_STORAGE[10];
}

/**
 * Get cache storage capacity
 */
export function getVaultStorage(level) {
    return CACHE_STORAGE[level] || CACHE_STORAGE[10];
}

/**
 * Get basilica storage capacity
 */
export function getKeepStorage(level) {
    return KEEP_STORAGE[level] || KEEP_STORAGE[10];
}

/**
 * Get maximum building slots from Keep level
 * Note: Town Hall takes 1 slot, so max placeable buildings = slots - 1
 */
export function getKeepBuildingSlots(level) {
    return BASILICA_BUILDING_SLOTS[level] || BASILICA_BUILDING_SLOTS[10];
}

/**
 * Get the minimum Keep level required to build a building
 * @param {number} buildingType - Building constant (e.g., CUTTER, FARM)
 * @returns {number} Minimum Keep level required (1-10), or 1 if not found
 */
export function getBuildingKeepRequirement(buildingType) {
    return BUILDING_KEEP_REQUIREMENTS[buildingType] || 1;
}

/**
 * Check if a building can be built at the current Keep level
 * @param {number} buildingType - Building constant
 * @param {number} keepLevel - Current Keep level
 * @returns {boolean} True if the building can be built
 */
export function canBuildBuilding(buildingType, keepLevel) {
    const requiredLevel = getBuildingKeepRequirement(buildingType);
    return keepLevel >= requiredLevel;
}

/**
 * Get building cost for a specific level
 * @param {number} buildingType - Building constant (e.g., CUTTER, FARM)
 * @param {number} level - Building level (1-10)
 * @returns {{wood: number, stone: number, buildTime: number}} Cost object with wood, stone costs, and build time in seconds
 */
export function getBuildingCost(buildingType, level) {
    const costs = BUILDING_COSTS[buildingType];
    if (!costs) {
        return { wood: 0, stone: 0, buildTime: 0 };
    }
    return costs[level] || costs[10] || { wood: 0, stone: 0, buildTime: 0 };
}

/**
 * Get total cost to build from level 1 to target level
 * @param {number} buildingType - Building constant
 * @param {number} targetLevel - Target level (1-10)
 * @returns {{wood: number, stone: number, buildTime: number}} Total cost and build time
 */
export function getTotalCostToLevel(buildingType, targetLevel) {
    const costs = BUILDING_COSTS[buildingType];
    if (!costs) {
        return { wood: 0, stone: 0, buildTime: 0 };
    }
    
    let totalWood = 0;
    let totalStone = 0;
    let totalBuildTime = 0;
    
    for (let level = 1; level <= targetLevel && level <= 10; level++) {
        const cost = costs[level] || { wood: 0, stone: 0, buildTime: 0 };
        totalWood += cost.wood;
        totalStone += cost.stone;
        totalBuildTime += cost.buildTime || 0;
    }
    
    return { wood: totalWood, stone: totalStone, buildTime: totalBuildTime };
}

/**
 * Format build time in seconds to human-readable string
 * @param {number} seconds - Build time in seconds
 * @returns {string} Formatted time string (e.g., "1 hr 30 min 15 sec")
 */
export function formatBuildTime(seconds) {
    if (!seconds || seconds === 0) return '0 sec';
    
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hr`);
    if (minutes > 0) parts.push(`${minutes} min`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs} sec`);
    
    return parts.join(' ');
}

